'use client';

import { motion } from 'framer-motion';
import type { PageSection } from '@/lib/cms';

import { getOrigin } from '@/lib/env';

const getBaseUrl = () => getOrigin();

function imageUrl(path?: string | null): string | null {
  if (!path) return null;
  const base = getBaseUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export function CmsTestimonialsSection({ section }: { section: PageSection }) {
  const items = section.items ?? [];
  const title = section.title ?? 'Testimonials';
  const description = section.content;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-plaster">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-soot">{title}</h2>
          {description && (
            <p className="mt-2 text-moss max-w-2xl mx-auto">{description}</p>
          )}
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, i) => {
            const img = imageUrl(item.image);
            return (
              <motion.div
                key={item.id ?? i}
                className="p-6 rounded-2xl bg-bg border border-landing-border shadow-sm hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                {item.description && (
                  <p className="text-landing-muted italic">&ldquo;{item.description}&rdquo;</p>
                )}
                <div className="mt-4 flex items-center gap-3">
                  {img ? (
                    <img
                      src={img}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover ring-2 ring-moss/20"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-moss/20 flex items-center justify-center text-moss font-semibold">
                      {(item.title ?? '?')[0]}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-soot">{item.title ?? 'Anonymous'}</p>
                    {item.subtitle && (
                      <p className="text-sm text-moss">{item.subtitle}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
